import React from "react";
import { render, screen, waitFor } from "@testing-library/react";
import { MemoryRouter, Route, Routes } from "react-router-dom";
import PublicDocumentViewer from "./PublicDocumentViewer";

const mockGetSyncAuthToken = jest.fn();

jest.mock("../../../config/runtimeConfig", () => ({
  getApiBaseUrl: () => "",
}));

jest.mock("../../../utils/getSyncAuthToken", () => ({
  getSyncAuthToken: () => mockGetSyncAuthToken(),
}));

jest.mock("../hooks/useMMDataBridge", () => ({
  useMMDataBridge: () => ({ current: null }),
}));

jest.mock("react-markdown", () => ({
  __esModule: true,
  default: ({ children }: { children?: unknown }) => {
    const React = require("react");
    return React.createElement(React.Fragment, null, children);
  },
}));

// remark-gfm is ESM-only; stub it as a no-op plugin so Jest (CJS) can load the
// component, which now passes remarkPlugins={[remarkGfm]} to ReactMarkdown.
jest.mock("remark-gfm", () => ({
  __esModule: true,
  default: () => {},
}));

describe("PublicDocumentViewer auth return", () => {
  const originalLocation = window.location;
  const mockFetch = jest.fn();
  const mockReplace = jest.fn();
  const readBlobText = (blob: Blob): Promise<string> => {
    if (typeof blob.text === "function") {
      return blob.text();
    }
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsText(blob);
    });
  };

  beforeEach(() => {
    mockFetch.mockReset();
    mockReplace.mockReset();
    mockGetSyncAuthToken.mockReturnValue(null);
    sessionStorage.clear();
    (global as any).fetch = mockFetch;
    Object.defineProperty(URL, "createObjectURL", {
      configurable: true,
      value: jest.fn(() => "blob:test-default"),
    });
    Object.defineProperty(URL, "revokeObjectURL", {
      configurable: true,
      value: jest.fn(),
    });
    delete (window as any).location;
    (window as any).location = {
      ...originalLocation,
      pathname: "/public/documents/workspace%2Fdoc-1",
      search: "?preview=1",
      hash: "#page-2",
      replace: mockReplace,
    };
  });

  afterEach(() => {
    delete (window as any).location;
    (window as any).location = originalLocation;
  });

  it("preserves the deep link when org-public documents require login", async () => {
    mockFetch.mockResolvedValueOnce({
      ok: false,
      status: 401,
      json: jest.fn().mockResolvedValue({}),
    });

    render(
      <MemoryRouter initialEntries={["/public/documents/workspace%2Fdoc-1"]}>
        <Routes>
          <Route
            path="/public/documents/*"
            element={<PublicDocumentViewer />}
          />
        </Routes>
      </MemoryRouter>,
    );

    await waitFor(() =>
      expect(mockReplace).toHaveBeenCalledWith(
        "/auth/return?to=%2Fpublic%2Fdocuments%2Fworkspace%252Fdoc-1%3Fpreview%3D1%23page-2",
      ),
    );
  });

  it("redirects a tokenless 401 only once per document, then explains the access failure", async () => {
    mockFetch.mockResolvedValue({
      ok: false,
      status: 401,
      json: jest.fn().mockResolvedValue({}),
    });

    const view = render(
      <MemoryRouter initialEntries={["/public/documents/workspace%2Fdoc-1"]}>
        <Routes>
          <Route
            path="/public/documents/*"
            element={<PublicDocumentViewer />}
          />
        </Routes>
      </MemoryRouter>,
    );

    await waitFor(() => expect(mockReplace).toHaveBeenCalledTimes(1));
    view.unmount();

    render(
      <MemoryRouter initialEntries={["/public/documents/workspace%2Fdoc-1"]}>
        <Routes>
          <Route
            path="/public/documents/*"
            element={<PublicDocumentViewer />}
          />
        </Routes>
      </MemoryRouter>,
    );

    expect(
      await screen.findByText(/sign in did not grant access to this document/i),
    ).toBeTruthy();
    expect(mockReplace).toHaveBeenCalledTimes(1);
  });

  it("injects mmData for published HTML even when no assets are resolvable", async () => {
    const html =
      "<!doctype html><html><head></head><body>No assets</body></html>";
    let capturedBlob: Blob | undefined;
    const mockCreateObjectURL = jest.fn((blob: Blob) => {
      capturedBlob = blob;
      return "blob:test-html";
    });
    Object.defineProperty(URL, "createObjectURL", {
      configurable: true,
      value: mockCreateObjectURL,
    });
    Object.defineProperty(URL, "revokeObjectURL", {
      configurable: true,
      value: jest.fn(),
    });

    mockFetch.mockResolvedValueOnce({
      ok: true,
      headers: {
        get: (name: string) => {
          if (name === "Content-Type") return "text/html";
          if (name === "X-Document-Filename") return "dashboard.html";
          return "";
        },
      },
      blob: jest.fn().mockResolvedValue({
        text: jest.fn().mockResolvedValue(html),
      }),
    });
    // This lane has no content host configured, so the viewer uses its
    // compatibility inline-preview path after checking for a session URL.
    mockFetch.mockResolvedValueOnce({
      ok: true,
      json: jest.fn().mockResolvedValue({ configured: false }),
    });

    render(
      <MemoryRouter initialEntries={["/public/documents/workspace%2Fdoc-1"]}>
        <Routes>
          <Route
            path="/public/documents/*"
            element={<PublicDocumentViewer />}
          />
        </Routes>
      </MemoryRouter>,
    );

    await waitFor(() => expect(mockCreateObjectURL).toHaveBeenCalled());
    expect(mockFetch).toHaveBeenCalledTimes(2);
    expect(capturedBlob).toBeDefined();

    const processedHtml = await readBlobText(capturedBlob as Blob);
    expect(processedHtml).toContain("window.mmData = {");
    expect(processedHtml).toContain("__sendMutation(filename, 'set'");
  });
});
